import { useEffect, useMemo, useState } from 'react';
import { UserExport } from '@mark43/cad-api';
import { isExportExpired } from '../common/helpers/files';
import { useExportsApi } from './use-exports-api';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';

interface NonExpiredExportsApi {
    currentExports: UserExport[];
    deleteExport: (e: UserExport) => void;
    deleteAllExports: (mediaAttachmentsEnabled?: boolean) => void;
    refetchExports: () => void;
    isLoading: boolean;
}

/**
 * Hook that wraps useExportsApi and filters out exports that were already expired
 * when the component's initial data load completed. Exports that expire while the
 * component is mounted remain visible in a disabled state.
 *
 * The filtering only happens once during initial load - subsequent refetches or
 * websocket updates will not hide additional exports, even if they are expired.
 *
 * This hook should be used by display components (export tray, menu, navigation)
 * to ensure accurate badge counts and hide stale exports on page load.
 *
 * Export generation hooks should use useExportsApi directly.
 *
 * @returns Filtered exports and all useExportsApi functions
 */
export function useNonExpiredExports(): NonExpiredExportsApi {
    const {
        currentExports: exports,
        isLoading,
        deleteExport,
        deleteAllExports,
        refetchExports,
    } = useExportsApi();
    const { mediaAttachmentsEnabled } = useEnabledFeatures();

    /**
     * Capture expired IDs at initial load time (frozen forever).
     * These are the export IDs that were already expired when the component
     * first loaded data. This set never changes after initialization, ensuring
     * that refetches and websocket updates don't hide additional exports.
     */
    const [disabledExportIds, setDisabledExportIds] = useState<Set<string> | null>(null);

    useEffect(
        function captureInitialExpiredExports() {
            if (!isLoading && disabledExportIds === null) {
                setDisabledExportIds(
                    new Set(
                        exports
                            .filter((ex) => isExportExpired(ex, mediaAttachmentsEnabled))
                            .map((ex) => ex.id)
                    )
                );
            }
        },
        [isLoading, exports, mediaAttachmentsEnabled, disabledExportIds]
    );

    const currentExports = useMemo(() => {
        if (!mediaAttachmentsEnabled) {
            return exports;
        }

        if (disabledExportIds === null) {
            // Still initializing, return empty to avoid showing unfiltered data
            return [];
        }
        // Filter using the frozen set from initial load
        return exports.filter((ex) => !disabledExportIds.has(ex.id));
    }, [exports, disabledExportIds, mediaAttachmentsEnabled]);

    return {
        currentExports,
        deleteExport,
        deleteAllExports,
        refetchExports,
        isLoading,
    };
}
